import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Check, AlertCircle, Network } from 'lucide-react';

import { EndpointInsert, ENDPOINT_CATEGORIES, IT_CATEGORIES, OT_CATEGORIES, SHARED_CATEGORIES } from '@/types/endpoint';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { 
  validateIP, validateMAC, validateVLAN, validateGateway, validateSubnetMask,
  isValidIP, isValidMAC, isValidVLAN, isValidGateway, isValidSubnetMask,
  formatMAC
} from '@/utils/networkValidation';
import { UserSelector } from '@/components/shared/UserSelector';

interface EndpointFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  systemId?: string;
  parentDomain?: 'IT' | 'OT' | 'DMZ' | 'Cloud';
  onSuccess?: () => void;
}

export function EndpointFormDialog({ open, onOpenChange, systemId, parentDomain, onSuccess }: EndpointFormDialogProps) {
  const [loading, setLoading] = useState(false);
  const [inheritedDomain, setInheritedDomain] = useState<string | undefined>(parentDomain);
  const [ownerId, setOwnerId] = useState<string | undefined>();
  const [formData, setFormData] = useState<Partial<EndpointInsert>>({
    name: '',
    type: '',
    asset_type: undefined,
    ip: '',
    mac: '',
    vendor: '',
    model: '',
    serial: '',
    os: '',
    firmware: '',
    vlan_id: undefined,
    default_gateway: '',
    subnet_mask: '',
    system_id: systemId,
  });

  // Validation errors
  const [ipError, setIpError] = useState<string | undefined>();
  const [macError, setMacError] = useState<string | undefined>();
  const [vlanError, setVlanError] = useState<string | undefined>();
  const [gatewayError, setGatewayError] = useState<string | undefined>();
  const [subnetError, setSubnetError] = useState<string | undefined>();

  // Fetch parent domain from system → zone if not passed as prop
  useEffect(() => {
    if (parentDomain) {
      setInheritedDomain(parentDomain);
      return;
    }
    
    if (systemId && open) {
      supabase
        .from('systems')
        .select('zone_id')
        .eq('id', systemId)
        .single()
        .then(({ data: systemData }) => {
          if (systemData?.zone_id) {
            supabase
              .from('zones')
              .select('domain')
              .eq('id', systemData.zone_id)
              .single()
              .then(({ data: zoneData }) => {
                if (zoneData?.domain) {
                  setInheritedDomain(zoneData.domain);
                }
              });
          }
        });
    }
  }, [systemId, parentDomain, open]);

  // Filter categories based on inherited domain
  const availableCategories = useMemo(() => {
    if (inheritedDomain === 'IT') {
      return [...IT_CATEGORIES, ...SHARED_CATEGORIES];
    }
    if (inheritedDomain === 'OT') {
      return [...OT_CATEGORIES, ...SHARED_CATEGORIES];
    }
    // Show all categories if domain is unknown
    return Object.keys(ENDPOINT_CATEGORIES);
  }, [inheritedDomain]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: '',
        type: '',
        asset_type: undefined,
        ip: '',
        mac: '',
        vendor: '',
        model: '',
        serial: '',
        os: '',
        firmware: '',
        vlan_id: undefined,
        default_gateway: '',
        subnet_mask: '',
        system_id: systemId,
      });
      setOwnerId(undefined);
      setIpError(undefined);
      setMacError(undefined);
      setVlanError(undefined);
      setGatewayError(undefined);
      setSubnetError(undefined);
    }
  }, [open, systemId]);

  const handleIpChange = (value: string) => {
    setFormData({ ...formData, ip: value });
    if (value.trim()) {
      const result = validateIP(value);
      setIpError(result.valid ? undefined : result.message);
    } else {
      setIpError(undefined);
    }
  };

  const handleMacChange = (value: string) => {
    setFormData({ ...formData, mac: value });
    if (value.trim()) {
      const result = validateMAC(value);
      setMacError(result.valid ? undefined : result.message);
    } else {
      setMacError(undefined);
    }
  };

  const handleMacBlur = () => {
    if (formData.mac && isValidMAC(formData.mac)) {
      setFormData({ ...formData, mac: formatMAC(formData.mac) });
    }
  };

  const handleVlanChange = (value: string) => {
    const vlanNum = value.trim() ? parseInt(value, 10) : undefined;
    setFormData({ ...formData, vlan_id: vlanNum });
    if (value.trim()) {
      const result = validateVLAN(value);
      setVlanError(result.valid ? undefined : result.message);
    } else {
      setVlanError(undefined);
    }
  };

  const handleGatewayChange = (value: string) => {
    setFormData({ ...formData, default_gateway: value });
    if (value.trim()) {
      const result = validateGateway(value);
      setGatewayError(result.valid ? undefined : result.message);
    } else {
      setGatewayError(undefined);
    }
  };

  const handleSubnetChange = (value: string) => {
    setFormData({ ...formData, subnet_mask: value });
    if (value.trim()) {
      const result = validateSubnetMask(value);
      setSubnetError(result.valid ? undefined : result.message);
    } else {
      setSubnetError(undefined);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.type) {
      toast.error('Name and category are required');
      return;
    }

    // Validate network fields
    if (formData.ip && !isValidIP(formData.ip)) {
      toast.error('Invalid IP address format');
      return;
    }
    if (formData.mac && !isValidMAC(formData.mac)) {
      toast.error('Invalid MAC address format');
      return;
    }
    if (formData.vlan_id && !isValidVLAN(formData.vlan_id)) {
      toast.error('Invalid VLAN ID (must be 1-4094)');
      return;
    }
    if (formData.default_gateway && !isValidGateway(formData.default_gateway)) {
      toast.error('Invalid default gateway IP');
      return;
    }
    if (formData.subnet_mask && !isValidSubnetMask(formData.subnet_mask)) {
      toast.error('Invalid subnet mask');
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', session.user.id)
        .single();

      if (!profile?.org_id) throw new Error('No organization found');

      const insertData: any = {
        ...formData,
        name: formData.name!,
        type: formData.type!,
        category: inheritedDomain, // Auto-set from parent domain
        mac: formData.mac ? formatMAC(formData.mac) : undefined,
        org_id: profile.org_id,
        system_id: systemId,
        owner_id: ownerId,
      };

      const { error } = await supabase
        .from('endpoints')
        .insert([insertData]);

      if (error) throw error;

      toast.success('Endpoint created successfully');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating endpoint:', error);
      toast.error('Failed to create endpoint');
    } finally {
      setLoading(false);
    }
  };

  const getDomainBadgeColor = (domain?: string) => {
    switch (domain) {
      case 'IT': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'OT': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'DMZ': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'Cloud': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const renderValidatedInput = (
    id: string,
    label: string,
    value: string | undefined,
    onChange: (value: string) => void,
    error: string | undefined,
    placeholder: string,
    onBlur?: () => void,
    type?: string
  ) => (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={type || 'text'}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          className={error ? 'border-destructive pr-10' : value && !error ? 'border-green-500 pr-10' : ''}
        />
        {value && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {error ? (
              <AlertCircle className="h-4 w-4 text-destructive" />
            ) : (
              <Check className="h-4 w-4 text-green-500" />
            )}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Add Endpoint
            {inheritedDomain && (
              <Badge variant="outline" className={cn('ml-2', getDomainBadgeColor(inheritedDomain))}>
                {inheritedDomain} Domain
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter endpoint name"
                required
              />
            </div>

            <div>
              <Label htmlFor="type">Category *</Label>
              <Select 
                value={formData.type || ''} 
                onValueChange={(value) => setFormData({ ...formData, type: value, asset_type: undefined })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {availableCategories.map((category) => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="asset_type">Asset Type</Label>
              <Select 
                value={formData.asset_type || ''} 
                onValueChange={(value) => setFormData({ ...formData, asset_type: value as any })}
                disabled={!formData.type}
              >
                <SelectTrigger>
                  <SelectValue placeholder={formData.type ? "Select asset type" : "Select category first"} />
                </SelectTrigger>
                <SelectContent>
                  {formData.type && ENDPOINT_CATEGORIES[formData.type as keyof typeof ENDPOINT_CATEGORIES]?.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Network Configuration */}
          <div className="space-y-4 p-3 border rounded-md bg-muted/30">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Network className="h-4 w-4" />
              Network Configuration
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              {renderValidatedInput('ip', 'IP Address', formData.ip, handleIpChange, ipError, '192.168.1.1')}
              {renderValidatedInput('mac', 'MAC Address', formData.mac, handleMacChange, macError, '00:1A:2B:3C:4D:5E', handleMacBlur)}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label htmlFor="vlan">VLAN ID</Label>
                <div className="relative">
                  <Input
                    id="vlan"
                    type="number"
                    min="1"
                    max="4094"
                    value={formData.vlan_id || ''}
                    onChange={(e) => handleVlanChange(e.target.value)}
                    placeholder="1-4094"
                    className={vlanError ? 'border-destructive pr-10' : formData.vlan_id && !vlanError ? 'border-green-500 pr-10' : ''}
                  />
                  {formData.vlan_id && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {vlanError ? (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      ) : (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  )}
                </div>
                {vlanError && <p className="text-xs text-destructive">{vlanError}</p>}
              </div>

              {renderValidatedInput('gateway', 'Default Gateway', formData.default_gateway, handleGatewayChange, gatewayError, '192.168.1.1')}
              {renderValidatedInput('subnet', 'Subnet Mask', formData.subnet_mask, handleSubnetChange, subnetError, '255.255.255.0')}
            </div>
          </div>

          {/* Hardware Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="vendor">Vendor</Label>
              <Input
                id="vendor"
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                placeholder="Vendor name"
              />
            </div>

            <div>
              <Label htmlFor="model">Model</Label>
              <Input
                id="model"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                placeholder="Model number"
              />
            </div>

            <div>
              <Label htmlFor="serial">Serial Number</Label>
              <Input
                id="serial"
                value={formData.serial}
                onChange={(e) => setFormData({ ...formData, serial: e.target.value })}
                placeholder="Serial number"
              />
            </div>

            <div>
              <Label htmlFor="os">Operating System</Label>
              <Input
                id="os"
                value={formData.os}
                onChange={(e) => setFormData({ ...formData, os: e.target.value })}
                placeholder="e.g., Windows Server 2019"
              />
            </div>

            <div>
              <Label htmlFor="firmware">Firmware</Label>
              <Input
                id="firmware"
                value={formData.firmware}
                onChange={(e) => setFormData({ ...formData, firmware: e.target.value })}
                placeholder="Firmware version"
              />
            </div>
          </div>

          <UserSelector
            value={ownerId}
            onChange={setOwnerId}
            label="Owner"
            placeholder="Select owner"
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Add Endpoint'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
